/**
 * 企业账号自定义商店名映射。
 *
 * 产品名称下拉的列表数据仍来自存量接口 `selectDbtIdByStudioId`，
 * 这里只额外拉一次 `/enterpriseAccount/product/list`，按 dbtId 匹配出自定义商店名，
 * 用于覆盖下拉的展示名——即原先写死在 `data/revenueDisplayName.ts` 里那张映射表的真实来源。
 */
import type { InjectionKey, Ref } from 'vue'
import { OK } from '@/common'
import { getProductList } from '@/api/lawyerBoard'

/** dbtId -> 自定义商店名，只收录有值的项 */
export type CustomStoreNameMap = Record<string, string>

export interface CustomStoreNameContext {
  names: Ref<CustomStoreNameMap>
  /** 映射加载完成的信号；下拉取名前 await 它，避免首次渲染拿到空映射 */
  ready: Promise<void>
}

export const CUSTOM_STORE_NAME_KEY: InjectionKey<CustomStoreNameContext> = Symbol('customStoreName')

/** 单页条数与页数上限：接口分页，翻完为止，上限用于兜底防止异常 total 导致死循环 */
const PAGE_SIZE = 200
const MAX_PAGES = 20

/** 拉取当前企业账号名下全部产品的自定义商店名 */
export const loadCustomStoreNames = async (enterpriseAccountId: string): Promise<CustomStoreNameMap> => {
  const result: CustomStoreNameMap = {}
  if (!enterpriseAccountId) return result

  let pageIndex = 1
  let total = 0
  do {
    const res = await getProductList({ accountId: enterpriseAccountId, pageIndex, pageSize: PAGE_SIZE })
    if (!res || res.code !== OK) break
    res.data.list.forEach((item) => {
      if (item.customName) result[item.productId] = item.customName
    })
    total = res.data.total
    pageIndex += 1
  } while ((pageIndex - 1) * PAGE_SIZE < total && pageIndex <= MAX_PAGES)

  return result
}
